/**
 * Ставит сгенерированное изделие в центр поля чертежа и подбирает общий масштаб.
 * Координаты листа совпадают с базовым холстом Canvas (без responsiveScale):
 * масштаб сцены и размер холста сокращаются, центр в мировых единицах — sheet / zoom.
 *
 * Поле — внутренняя область листа над штампом и примечанием «Размер для справок».
 * Снимок IGES и прочие metadata не сдвигаются: экспорт считает отверстия
 * относительно трубы, положение на листе на модель не влияет.
 */

const WORK_AREA = {
  landscape: { x: 40, y: 40, width: 1506, height: 750 },
  portrait: { x: 36, y: 40, width: 721, height: 820 },
};

const ZOOM_MIN = 0.1;
const ZOOM_MAX = 5;
/** Запас вокруг изделия, размерных линий и выносок, мм. */
const CONTENT_MARGIN = 100;

function createBounds() {
  return {
    minX: Infinity,
    minY: Infinity,
    maxX: -Infinity,
    maxY: -Infinity,
  };
}

function boundsAreValid(bounds) {
  return Number.isFinite(bounds.minX) && Number.isFinite(bounds.maxX)
    && bounds.maxX > bounds.minX
    && Number.isFinite(bounds.minY) && Number.isFinite(bounds.maxY)
    && bounds.maxY > bounds.minY;
}

function expand(bounds, x, y) {
  if (!Number.isFinite(x) || !Number.isFinite(y)) return;
  if (x < bounds.minX) bounds.minX = x;
  if (y < bounds.minY) bounds.minY = y;
  if (x > bounds.maxX) bounds.maxX = x;
  if (y > bounds.maxY) bounds.maxY = y;
}

function expandRect(bounds, obj) {
  const width = Number(obj.width) || 0;
  const height = Number(obj.height) || 0;
  const x = Number(obj.x) || 0;
  const y = Number(obj.y) || 0;
  const groupX = Number(obj.groupX) || 0;
  const groupY = Number(obj.groupY) || 0;
  const offsetX = Number(obj.offsetX) || 0;
  const offsetY = Number(obj.offsetY) || 0;
  const rotation = ((Number(obj.rotation) || 0) * Math.PI) / 180;
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);

  const corners = [
    [x, y],
    [x + width, y],
    [x + width, y + height],
    [x, y + height],
  ];

  corners.forEach(([lx, ly]) => {
    const px = lx - offsetX;
    const py = ly - offsetY;
    expand(bounds, groupX + px * cos - py * sin, groupY + px * sin + py * cos);
  });
}

function expandDimension(bounds, dim) {
  if (!dim) return;
  if (dim.isHorizontal) {
    const y = dim.position && Number(dim.position.y);
    if (Number.isFinite(y)) {
      expand(bounds, dim.x1, y);
      expand(bounds, dim.x2, y);
    }
    expand(bounds, dim.x1, dim.y1);
    expand(bounds, dim.x2, dim.y2);
    return;
  }

  const x = dim.position && Number(dim.position.x);
  if (Number.isFinite(x)) {
    expand(bounds, x, dim.y1);
    expand(bounds, x, dim.y2);
  }
  expand(bounds, dim.x1, dim.y1);
  expand(bounds, dim.x2, dim.y2);
}

function shiftNumber(value, delta) {
  return typeof value === 'number' && Number.isFinite(value) ? value + delta : value;
}

function shiftCylinder(cylinder, dx, dy) {
  return {
    ...cylinder,
    x: shiftNumber(cylinder.x, dx),
    y: shiftNumber(cylinder.y, dy),
  };
}

function shiftFitting(fitting, dx, dy) {
  if (typeof fitting.groupX === 'number' || typeof fitting.groupY === 'number') {
    return {
      ...fitting,
      groupX: shiftNumber(fitting.groupX ?? 0, dx),
      groupY: shiftNumber(fitting.groupY ?? 0, dy),
    };
  }
  return {
    ...fitting,
    x: shiftNumber(fitting.x, dx),
    y: shiftNumber(fitting.y, dy),
  };
}

function shiftDimension(dim, dx, dy) {
  const next = {
    ...dim,
    x1: shiftNumber(dim.x1, dx),
    y1: shiftNumber(dim.y1, dy),
    x2: shiftNumber(dim.x2, dx),
    y2: shiftNumber(dim.y2, dy),
  };
  if (dim.position) {
    next.position = {
      ...dim.position,
      x: shiftNumber(dim.position.x, dx),
      y: shiftNumber(dim.position.y, dy),
    };
  }
  if (Array.isArray(dim.elements)) {
    next.elements = dim.elements.map((element) => ({
      ...element,
      x: shiftNumber(element.x, dx),
      y: shiftNumber(element.y, dy),
    }));
  }
  return next;
}

function shiftLine(line, dx, dy) {
  if (!line || !Array.isArray(line.points)) return line;
  const points = line.points.map((value, index) => (
    typeof value === 'number' ? value + (index % 2 === 0 ? dx : dy) : value
  ));
  return { ...line, points };
}

/**
 * @param {object} drawingData результат генератора автоконструктора
 * @returns {object} тот же чертёж со сдвинутой геометрией и settings.zoom
 */
export function fitGeneratedDrawingToSheet(drawingData) {
  if (!drawingData) return drawingData;

  const cylinders = drawingData.cylinders || [];
  const fittings = drawingData.fittings || [];
  if (cylinders.length === 0 && fittings.length === 0) return drawingData;

  const product = createBounds();
  cylinders.forEach((cylinder) => expandRect(product, cylinder));
  fittings.forEach((fitting) => expandRect(product, fitting));
  if (!boundsAreValid(product)) return drawingData;

  const content = {
    minX: product.minX,
    minY: product.minY,
    maxX: product.maxX,
    maxY: product.maxY,
  };
  (drawingData.balloons || []).forEach((balloon) => expand(content, balloon.x, balloon.y));
  (drawingData.dimensionLines || []).forEach((dim) => expandDimension(content, dim));

  const isLandscape = drawingData.settings?.isLandscape === true;
  const work = isLandscape ? WORK_AREA.landscape : WORK_AREA.portrait;

  const productCenterX = (product.minX + product.maxX) / 2;
  const productCenterY = (product.minY + product.maxY) / 2;
  const halfWidth = Math.max(
    productCenterX - content.minX,
    content.maxX - productCenterX,
  ) + CONTENT_MARGIN;
  const halfHeight = Math.max(
    productCenterY - content.minY,
    content.maxY - productCenterY,
  ) + CONTENT_MARGIN;

  const fittedZoom = Math.min(work.width / (halfWidth * 2), work.height / (halfHeight * 2));
  const zoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, Math.round(fittedZoom * 100) / 100));

  const viewCenterX = (work.x + work.width / 2) / zoom;
  const viewCenterY = (work.y + work.height / 2) / zoom;
  const dx = viewCenterX - productCenterX;
  const dy = viewCenterY - productCenterY;

  return {
    ...drawingData,
    cylinders: cylinders.map((cylinder) => shiftCylinder(cylinder, dx, dy)),
    fittings: fittings.map((fitting) => shiftFitting(fitting, dx, dy)),
    welds: (drawingData.welds || []).map((weld) => ({
      ...weld,
      x: shiftNumber(weld.x, dx),
      y: shiftNumber(weld.y, dy),
    })),
    balloons: (drawingData.balloons || []).map((balloon) => ({
      ...balloon,
      x: shiftNumber(balloon.x, dx),
      y: shiftNumber(balloon.y, dy),
    })),
    dimensionLines: (drawingData.dimensionLines || []).map((dim) => shiftDimension(dim, dx, dy)),
    lines: (drawingData.lines || []).map((line) => shiftLine(line, dx, dy)),
    settings: {
      ...(drawingData.settings || {}),
      zoom,
    },
  };
}
